import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { env } from '@/shared/config/env'
import { MANAGERIUM_QUERY_KEY, ONE_HOUR_MS } from '@/shared/api/queryClient'
import {
  getDashboardCard,
  type CardFilters,
  getDashboardStructure,
} from '@/features/dashboard/api/client'

const DASHBOARD_ID = env.dashboardId

const dashboardKeys = {
  all: [MANAGERIUM_QUERY_KEY, 'dashboard', DASHBOARD_ID] as const,
  card: (cardId: number, filters: CardFilters) =>
    [...dashboardKeys.all, 'card', cardId, filters] as const,
}

export const useDashboardStructure = () =>
  useQuery({
    queryKey: dashboardKeys.all,
    queryFn: () => getDashboardStructure(DASHBOARD_ID),
    staleTime: ONE_HOUR_MS,
  })

/**
 * A card, sliced by the filters if given. When they change the previous
 * numbers stay (`isPlaceholderData`) until the new ones arrive.
 */
export const useDashboardCard = (cardId: number, filters: CardFilters = {}) => {
  const { periods, timeframe, markets, deepDive, cLevel, criticality, tag } =
    filters
  const sent = {
    periods,
    timeframe,
    markets,
    deepDive,
    cLevel,
    criticality,
    tag,
  }
  const query = useQuery({
    queryKey: dashboardKeys.card(cardId, sent),
    queryFn: () => getDashboardCard(DASHBOARD_ID, cardId, sent),
    staleTime: ONE_HOUR_MS,
    placeholderData: keepPreviousData,
  })
  return { ...query, isRefreshing: query.isFetching && query.isPlaceholderData }
}
